// swatch-gallery.js - Fixed version with lazysizes neutralization
console.log('[SG] Fixed image mapping script loaded at', new Date().toISOString());

(function() {
    console.log('[SG] IIFE executing');
    
    const DEBUG = true;
    
    function log(...args) {
        if (DEBUG) console.log('[SG]', ...args);
    }
    
    class SwatchGallery {
        constructor() {
            log('SwatchGallery constructor called');
            this.config = null;
            this.currentColor = null;
            this.initialized = false;
            this.detectionMethods = [];
            this.swiperInstance = null;
            this.thumbSwiperInstance = null;
            this.updateTimeout = null;
            this.fragranceDescriptions = {};
            this.layoutType = null;
            this.sectionId = null;
            
            // Store original gallery state for style preservation
            this.originalGalleryState = {
                mainSlideTemplate: null,
                thumbnailTemplate: null,
                containerClasses: null,
                swiperConfig: null,
                slideAttributes: new Map(),
                themeSettings: {},
                originalSlides: [],
                originalThumbs: [],
                imageRatio: null
            };
            
            window.SwatchGalleryInstance = this;
            this.init();
        }
        
        // Helper: fully update an <img> element, neutralizing lazysizes data-* attrs
        // so the lazy loader doesn't re-apply the OLD image via srcset later.
        _updateImg(img, newSrc, newAlt, isFirst) {
            if (!img) return;
            img.src = newSrc;
            img.removeAttribute('srcset');
            img.removeAttribute('data-srcset');
            // Update lazysizes data-src so it points to the NEW image
            if (img.hasAttribute('data-src')) {
                img.setAttribute('data-src', newSrc.replace(/width=\d+/, 'width={width}'));
            }
            // Update data-original to prevent any fallback re-render
            if (img.hasAttribute('data-original')) {
                img.setAttribute('data-original', newSrc);
            }
            if (newAlt) {
                img.alt = newAlt;
            }
            img.loading = isFirst ? 'eager' : 'lazy';
            // Reset lazysizes state so it re-processes with the new URL
            img.classList.remove('lazyloaded');
            img.classList.add('lazyload');
        }

        // Helper function to normalize apostrophes and HTML entities
        normalizeApostrophes(str) {
            if (!str) return str;
            // Decode HTML entities first, then normalize all apostrophes to standard
            return str
                .replace(/&#39;/g, "'")  // HTML entity to standard apostrophe
                .replace(/&apos;/g, "'")  // Another HTML entity variant
                .replace(/'/g, "'")       // Curly apostrophe to standard
                .replace(/'/g, "'")       // Another curly variant to standard
                .replace(/`/g, "'");      // Backtick to standard (just in case)
        }
        
        init() {
            log('Initializing SwatchGallery');
            
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => this.setup());
            } else {
                setTimeout(() => this.setup(), 100);
            }
        }
        
        setup() {
            log('Setting up SwatchGallery');
            
            this.loadConfig();
            
            if (!this.config || !this.config.enabled) {
                log('Config not enabled or not found, exiting');
                return;
            }
            
            log('Config loaded:', this.config);
            
            // Detect layout type
            this.detectLayoutType();
            
            // Capture original gallery state BEFORE any modifications
            this.captureOriginalState();
            
            // Find and store Swiper instances
            this.findSwiperInstances();
            
            // Setup all detection methods
            this.setupDetectionMethods();
            
            // Get initial color
            this.detectInitialColor();
            
            // Watch for theme editor changes
            this.watchThemeEditor();
            
            this.initialized = true;
            log('SwatchGallery initialized successfully');
        }
        
        detectLayoutType() {
            // Check for Layout 2 specific elements
            const mediaGallery = document.querySelector('media-gallery[data-media-layout="thumbnails"]');
            const hasThumbnails = document.querySelector('.main--product-thumbnails');
            
            if (mediaGallery && hasThumbnails) {
                this.layoutType = 'layout_2';
                log('Detected Layout 2 (thumbnails gallery)');
            } else if (document.querySelector('.product-grid-layout_1')) {
                this.layoutType = 'layout_1';
                log('Detected Layout 1');
            } else if (document.querySelector('.product-grid-layout_3')) {
                this.layoutType = 'layout_3';
                log('Detected Layout 3');
            } else {
                this.layoutType = 'default';
                log('Using default layout detection');
            }
            
            // Get section ID
            const section = document.querySelector('[id^="shopify-section-"]');
            if (section) {
                this.sectionId = section.id.replace('shopify-section-', '');
                log('Section ID:', this.sectionId);
            }
        }
        
        captureOriginalState() {
            log('Capturing original gallery state for', this.layoutType);
            
            if (this.layoutType === 'layout_2') {
                this.captureLayout2State();
            } else {
                this.captureDefaultState();
            }
            
            // Capture theme settings from CSS custom properties
            const sectionElement = document.querySelector('[id^="shopify-section-"]');
            if (sectionElement) {
                const sectionStyles = getComputedStyle(sectionElement);
                this.originalGalleryState.themeSettings = {
                    imageRatio: sectionStyles.getPropertyValue('--image-ratio'),
                    borderRadius: sectionStyles.getPropertyValue('--carousel-border-radius'),
                    cardRadius: sectionStyles.getPropertyValue('--card_radius'),
                };
            }
            
            log('Original state captured:', this.originalGalleryState);
        }
        
        captureLayout2State() {
            // Capture the entire media-gallery structure for Layout 2
            const mediaGallery = document.querySelector('media-gallery');
            if (!mediaGallery) {
                log('Media gallery not found');
                return;
            }
            
            // Store image ratio
            const mediaDiv = mediaGallery.querySelector('.media[style*="--image_ratio"]');
            if (mediaDiv) {
                const style = mediaDiv.getAttribute('style');
                const ratioMatch = style.match(/--image_ratio:\s*([^;]+)/);
                if (ratioMatch) {
                    this.originalGalleryState.imageRatio = ratioMatch[1];
                }
            }
            
            // Store ALL original slides as complete templates
            const mainSlides = mediaGallery.querySelectorAll('.main--product-item.swiper-slide');
            this.originalGalleryState.originalSlides = Array.from(mainSlides).map(slide => {
                return this.extractCompleteSlideTemplate(slide);
            });
            
            // Take first slide as template
            if (mainSlides.length > 0) {
                this.originalGalleryState.mainSlideTemplate = this.extractLayout2SlideTemplate(mainSlides[0]);
            }
            
            // Store ALL original thumbnails
            const thumbs = mediaGallery.querySelectorAll('.main--product-thumbnail-item');
            this.originalGalleryState.originalThumbs = Array.from(thumbs).map(thumb => {
                return this.extractCompleteThumbnailTemplate(thumb);
            });
            
            // Take first thumbnail as template
            if (thumbs.length > 0) {
                this.originalGalleryState.thumbnailTemplate = this.extractLayout2ThumbnailTemplate(thumbs[0]);
            }
            
            log('Layout 2 state captured:', {
                slides: this.originalGalleryState.originalSlides.length,
                thumbs: this.originalGalleryState.originalThumbs.length,
                ratio: this.originalGalleryState.imageRatio
            });
        }
        
        extractCompleteSlideTemplate(slideElement) {
            return {
                html: slideElement.outerHTML,
                attributes: Array.from(slideElement.attributes).reduce((acc, attr) => {
                    acc[attr.name] = attr.value;
                    return acc;
                }, {}),
                mediaId: slideElement.getAttribute('data-media-id')
            };
        }
        
        extractCompleteThumbnailTemplate(thumbElement) {
            return {
                html: thumbElement.outerHTML,
                attributes: Array.from(thumbElement.attributes).reduce((acc, attr) => {
                    acc[attr.name] = attr.value;
                    return acc;
                }, {}),
                thumbId: thumbElement.getAttribute('data-thumb-id')
            };
        }
        
        extractLayout2SlideTemplate(slideElement) {
            const clone = slideElement.cloneNode(true);
            
            // Remove specific image but keep structure
            const img = clone.querySelector('img');
            if (img) {
                // Store all image attributes for preservation
                const imgAttrs = {};
                for (let attr of img.attributes) {
                    if (attr.name !== 'src' && attr.name !== 'srcset') {
                        imgAttrs[attr.name] = attr.value;
                    }
                }
                
                img.removeAttribute('src');
                img.removeAttribute('srcset');
                img.setAttribute('src', '__IMAGE_SRC__');
                img.setAttribute('alt', '__IMAGE_ALT__');
            }
            
            // Update data attributes
            clone.setAttribute('data-media-id', '__MEDIA_ID__');
            
            const zoomButton = clone.querySelector('media-zoom-button');
            if (zoomButton) {
                zoomButton.setAttribute('data-index', '__INDEX__');
            }
            
            return clone.outerHTML;
        }
        
        extractLayout2ThumbnailTemplate(thumbElement) {
            const clone = thumbElement.cloneNode(true);
            
            // Remove specific image but keep structure
            const img = clone.querySelector('img');
            if (img) {
                img.removeAttribute('src');
                img.removeAttribute('srcset');
                img.setAttribute('src', '__THUMB_SRC__');
                img.setAttribute('alt', '__THUMB_ALT__');
            }
            
            // Update data attributes
            clone.setAttribute('data-thumb-id', '__THUMB_ID__');
            
            return clone.outerHTML;
        }
        
        captureDefaultState() {
            // Original capture logic for other layouts
            const mainGallery = document.querySelector('.main--product-image-slider.swiper-wrapper') || 
                               document.querySelector('.swiper-wrapper');
            
            if (mainGallery) {
                this.originalGalleryState.containerClasses = mainGallery.className;
                
                const slides = mainGallery.querySelectorAll('.swiper-slide');
                this.originalGalleryState.originalSlides = Array.from(slides).map(slide => {
                    return {
                        html: slide.outerHTML,
                        classes: slide.className,
                        attributes: Array.from(slide.attributes).reduce((acc, attr) => {
                            acc[attr.name] = attr.value;
                            return acc;
                        }, {})
                    };
                });
                
                if (slides.length > 0) {
                    this.originalGalleryState.mainSlideTemplate = this.extractSlideTemplate(slides[0]);
                }
            }
            
            // Capture thumbnail template
            const thumbContainer = document.querySelector('.main--product-thumbnail-outer');
            if (thumbContainer) {
                const thumbs = thumbContainer.querySelectorAll('.swiper-slide');
                this.originalGalleryState.originalThumbs = Array.from(thumbs).map(thumb => {
                    return {
                        html: thumb.outerHTML,
                        classes: thumb.className,
                        attributes: Array.from(thumb.attributes).reduce((acc, attr) => {
                            acc[attr.name] = attr.value;
                            return acc;
                        }, {})
                    };
                });
                
                if (thumbs.length > 0) {
                    this.originalGalleryState.thumbnailTemplate = this.extractSlideTemplate(thumbs[0]);
                }
            }
        }
        
        extractSlideTemplate(slideElement) {
            const clone = slideElement.cloneNode(true);
            
            const img = clone.querySelector('img');
            if (img) {
                img.removeAttribute('src');
                img.removeAttribute('srcset');
                img.setAttribute('src', '__IMAGE_SRC__');
                img.setAttribute('alt', '__IMAGE_ALT__');
            }
            
            return {
                html: clone.outerHTML,
                classes: slideElement.className,
                attributes: Array.from(slideElement.attributes).reduce((acc, attr) => {
                    acc[attr.name] = attr.value;
                    return acc;
                }, {})
            };
        }
        
        loadConfig() {
            const configElement = document.getElementById('swatch-gallery-config');
            if (!configElement) {
                log('Config element not found');
                return;
            }
            
            try {
                this.config = JSON.parse(configElement.textContent);
                
                if (this.config.fragranceDescriptions) {
                    this.fragranceDescriptions = this.config.fragranceDescriptions;
                    log('Fragrance descriptions loaded:', this.fragranceDescriptions);
                }
                
                log('Config parsed successfully:', Object.keys(this.config.map || {}).length, 'colors mapped');
                
                // Debug: log the actual mapping
                if (this.config.map) {
                    Object.entries(this.config.map).forEach(([color, images]) => {
                        log(`Color "${color}" has ${images.length} images:`, images.map(img => img.src.split('/').pop()));
                    });
                }
            } catch (e) {
                console.error('[SG] Failed to parse config:', e);
            }
        }
        
        findSwiperInstances() {
            if (this.layoutType === 'layout_2') {
                // For Layout 2, find specific swiper instances
                const mainSliderOuter = document.querySelector('.main--product-image-slider-outer.swiper');
                if (mainSliderOuter && mainSliderOuter.swiper) {
                    this.swiperInstance = mainSliderOuter.swiper;
                    log('Layout 2 main Swiper found');
                }
                
                const thumbOuter = document.querySelector('.main--product-thumbnail-outer.swiper');
                if (thumbOuter && thumbOuter.swiper) {
                    this.thumbSwiperInstance = thumbOuter.swiper;
                    log('Layout 2 thumbnail Swiper found');
                }
            } else {
                // Original swiper finding logic
                const containers = document.querySelectorAll('.swiper-container, .swiper, .product__media');
                
                for (const container of containers) {
                    if (container.swiper && !container.classList.contains('main--product-thumbnail-outer')) {
                        this.swiperInstance = container.swiper;
                        log('Main Swiper instance found on:', container.className);
                        break;
                    }
                }
                
                // Find thumbnail swiper
                const thumbContainer = document.querySelector('.main--product-thumbnail-outer');
                if (thumbContainer && thumbContainer.swiper) {
                    this.thumbSwiperInstance = thumbContainer.swiper;
                    log('Thumbnail Swiper instance found');
                }
            }
            
            if (this.swiperInstance) {
                log('Main Swiper ready, loop mode:', this.swiperInstance.params.loop);
            }
            if (this.thumbSwiperInstance) {
                log('Thumbnail Swiper ready');
            }
        }
        
        setupDetectionMethods() {
            log('Setting up detection methods');
            
            // Method 1: Listen for Shopify variant change events
            ['variant:change', 'variantChange', 'variant-change'].forEach(eventName => {
                document.addEventListener(eventName, (e) => {
                    log(`Event detected: ${eventName}`, e.detail);
                    this.handleVariantChange(e.detail?.variant);
                });
            });
            
            // Method 2: Direct radio button monitoring (option radios + fragrance radios)
            document.addEventListener('change', (e) => {
                if (e.target.type === 'radio' && e.target.name && e.target.name.includes('option')) {
                    log('Radio changed directly:', e.target.value);
                    this.handleColorChange(e.target.value);
                }
                if (e.target.type === 'radio' && e.target.classList.contains('fragrance-radio')) {
                    const fragranceName = e.target.closest('.fragrance-swatch-item')?.dataset?.fragranceName;
                    if (fragranceName) {
                        log('Fragrance radio changed:', fragranceName);
                        this.handleColorChange(fragranceName);
                    }
                }
            }, true);
            
            // Method 3: Watch variant selectors
            this.watchVariantSelectors();
            
            log('Detection methods setup complete');
        }
        
        watchVariantSelectors() {
            const selectors = [
                '[name="Color"]',
                '[name="Colour"]',
                'input[type="radio"][name*="option"]',
            ];
            
            selectors.forEach(selector => {
                document.querySelectorAll(selector).forEach(element => {
                    element.addEventListener('change', (e) => {
                        log('Variant selector changed:', e.target.value);
                        this.handleColorChange(e.target.value);
                    });
                });
            });
        }
        
        detectInitialColor() {
            const color = this.detectColorFromDOM();
            if (color) {
                log('Initial color detected:', color);
                // Use handleColorChange so the gallery swap also fires (not just description)
                // Don't set currentColor directly — let handleColorChange do the full update
                this.handleColorChange(color);
            }
        }
        
        detectColorFromDOM() {
            // Check standard option radios
            const checkedRadio = document.querySelector('input[type="radio"][name*="option"]:checked');
            if (checkedRadio && checkedRadio.value) {
                log('Color detected from checked radio:', checkedRadio.value);
                return checkedRadio.value;
            }

            // Check fragrance radios — use URL param as source of truth
            // (radio name collision with quick-view drawer means :checked is unreliable)
            const urlVariantId = new URLSearchParams(window.location.search).get('variant');
            const mainPicker = document.querySelector('[data-fragrance-swatches]');
            if (mainPicker) {
                const targetRadio = urlVariantId
                    ? mainPicker.querySelector(`.fragrance-radio[value="${urlVariantId}"]`)
                    : mainPicker.querySelector('.fragrance-radio:checked')
                      || mainPicker.querySelector('.fragrance-swatch-item.selected .fragrance-radio');
                if (targetRadio) {
                    const fragranceName = targetRadio.closest('.fragrance-swatch-item')?.dataset?.fragranceName;
                    if (fragranceName) {
                        log('Color detected from fragrance radio:', fragranceName);
                        return fragranceName;
                    }
                }
            }

            return null;
        }
        
        handleVariantChange(variant) {
            if (!variant) return;
            
            log('Handling variant change:', variant);
            
            const color = variant.option1 || 
                         variant.options?.[0] || 
                         variant.title?.split(' - ')[0];
            
            if (color) {
                this.handleColorChange(color);
            }
        }
        
        handleColorChange(color) {
            if (!color || !this.config || !this.config.enabled) {
                log('Cannot handle color change:', {color, configLoaded: !!this.config, enabled: this.config?.enabled});
                return;
            }
            
            color = color.trim();
            
            if (color === this.currentColor) {
                log('Color unchanged:', color);
                return;
            }
            
            log('Color changed from', this.currentColor, 'to', color);
            this.currentColor = color;
            
            // Update the fragrance description
            this.updateFragranceDescription(color);
            
            if (this.updateTimeout) {
                clearTimeout(this.updateTimeout);
            }
            
            this.updateTimeout = setTimeout(() => {
                log('Delayed swap for:', color);
                this.swapGallery(color);
            }, 400);
        }
        
        // Helper function to find matching key with different apostrophe formats
        findMatchingKey(obj, searchKey) {
            // First try exact match
            if (obj[searchKey]) {
                log('Exact match found for:', searchKey);
                return searchKey;
            }
            
            // Normalize the search key
            const normalizedSearch = this.normalizeApostrophes(searchKey);
            log('Normalized search key:', normalizedSearch);
            
            // Try normalized direct match
            if (obj[normalizedSearch]) {
                log('Found with normalized key:', normalizedSearch);
                return normalizedSearch;
            }
            
            // Look through all keys and normalize them
            for (const key of Object.keys(obj)) {
                const normalizedKey = this.normalizeApostrophes(key);
                
                if (normalizedKey === normalizedSearch) {
                    log('Found matching key:', key, 'matches', searchKey);
                    return key;
                }
            }
            
            // Special handling for Santal D'Orange specifically
            if (searchKey.toLowerCase().includes('santal')) {
                log('Special Santal handling - checking all keys:');
                for (const key of Object.keys(obj)) {
                    log('  Checking key:', key);
                    if (key.toLowerCase().includes('santal')) {
                        log('  -> Found Santal key:', key);
                        return key;
                    }
                }
            }
            
            log('No matching key found for:', searchKey);
            return null;
        }
        
        updateFragranceDescription(fragranceName) {
            if (!this.config.isFragranceProduct) return;
            
            log('updateFragranceDescription called with:', fragranceName);
            log('Available description keys:', Object.keys(this.fragranceDescriptions));
            
            // Try to find matching description key
            const matchingKey = this.findMatchingKey(this.fragranceDescriptions, fragranceName);
            const description = matchingKey ? this.fragranceDescriptions[matchingKey] : null;
            
            log('Matching key found:', matchingKey);
            log('Description found:', description ? description.substring(0, 50) + '...' : 'NOT FOUND');
            
            let descContainer = document.querySelector('.fragrance-description-container');
            
            if (!descContainer) {
                const fragrancePicker = document.querySelector('[data-fragrance-swatches]');
                if (fragrancePicker && fragrancePicker.parentElement) {
                    descContainer = document.createElement('div');
                    descContainer.className = 'fragrance-description-container';
                    fragrancePicker.parentElement.insertBefore(descContainer, fragrancePicker.nextSibling);
                }
            }
            
            if (descContainer) {
                if (description) {
                    descContainer.innerHTML = `
                        <div class="fragrance-description-text">
                            ${description}
                        </div>
                    `;
                    descContainer.style.display = 'block';
                    
                    descContainer.classList.remove('fade-in');
                    void descContainer.offsetWidth;
                    descContainer.classList.add('fade-in');
                } else {
                    descContainer.style.display = 'none';
                }
            }
        }
        
        swapGallery(colorName) {
            log('swapGallery called with:', colorName);
            log('Available map keys:', Object.keys(this.config.map));
            
            // Try to find matching images key
            const matchingKey = this.findMatchingKey(this.config.map, colorName);
            const images = matchingKey ? this.config.map[matchingKey] : null;
            
            if (!images) {
                log('No images found for color:', colorName);
                log('Tried keys:', colorName, 'and normalized versions');
                return;
            }
            
            log('Found', images.length, 'images using key:', matchingKey);
            log('Images:', images.map(img => img.src.split('/').pop()));

            // Safety guard: don't swap if the matched set has fewer images than
            // the current gallery.  This prevents size-variant products (where
            // image alt text rarely matches "Full Size (50ml)") from collapsing
            // a 7-image gallery down to 1.
            const currentSlideCount = this.swiperInstance
                ? this.swiperInstance.slides.length
                : (document.querySelectorAll('.main--product-image-slider .swiper-slide, .swiper-wrapper .swiper-slide').length || 0);
            if (images.length < 2 && currentSlideCount > images.length) {
                log('Safety guard: matched', images.length, 'images but gallery has', currentSlideCount, '— skipping swap');
                return;
            }

            if (this.layoutType === 'layout_2') {
                this.swapLayout2Gallery(images);
            } else {
                this.swapDefaultGallery(images);
            }
            
            // Sync any theme styles that might have been applied
            this.syncThemeStyles();
            
            log('Gallery swap complete');
            
            // Trigger necessary events
            this.triggerGalleryUpdate();
        }
        
        swapLayout2Gallery(images) {
            log('Swapping Layout 2 gallery with', images.length, 'images');
            
            // Update main gallery
            this.updateLayout2MainGallery(images);
            
            // Update thumbnail gallery with offset
            this.updateLayout2ThumbnailGallery(images);
            
            // Re-setup synchronization
            this.setupLayout2GallerySync();
        }
        
        updateLayout2MainGallery(images) {
            if (!this.swiperInstance) {
                log('Missing swiper instance for Layout 2 main gallery');
                // Try direct DOM manipulation if swiper not available
                this.updateLayout2MainGalleryDOM(images);
                return;
            }
            
            log('Updating Layout 2 main gallery with', images.length, 'images');
            
            try {
                // Get current slides to preserve structure
                const currentSlides = this.swiperInstance.slides;
                
                // Update existing slides in place rather than removing/adding
                if (currentSlides && currentSlides.length > 0) {
                    currentSlides.forEach((slide, index) => {
                        if (index < images.length) {
                            const image = images[index];
                            const img = slide.querySelector('img');
                            this._updateImg(img, image.src, image.alt, index === 0);
                        }
                    });
                    
                    // If we have more images than slides, add new ones
                    if (images.length > currentSlides.length) {
                        for (let i = currentSlides.length; i < images.length; i++) {
                            const image = images[i];
                            let slideHtml;
                            
                            if (this.originalGalleryState.mainSlideTemplate) {
                                slideHtml = this.originalGalleryState.mainSlideTemplate;
                                slideHtml = slideHtml
                                    .replace(/__IMAGE_SRC__/g, image.src)
                                    .replace(/__IMAGE_ALT__/g, image.alt || '')
                                    .replace(/__MEDIA_ID__/g, `${this.sectionId}-gallery-${i}`)
                                    .replace(/__INDEX__/g, i.toString());
                            } else {
                                slideHtml = `
                                    <div class="swiper-slide main--product-item" data-media-id="${this.sectionId}-gallery-${i}">
                                        <div class="media" ${this.originalGalleryState.imageRatio ? `style="--image_ratio: ${this.originalGalleryState.imageRatio}"` : ''}>
                                            <img src="${image.src}" alt="${image.alt || ''}" loading="lazy">
                                        </div>
                                    </div>
                                `;
                            }
                            
                            this.swiperInstance.appendSlide(slideHtml);
                        }
                    }
                    
                    // If we have fewer images than slides, remove extra slides
                    if (images.length < currentSlides.length) {
                        for (let i = currentSlides.length - 1; i >= images.length; i--) {
                            this.swiperInstance.removeSlide(i);
                        }
                    }
                    
                } else {
                    // Fallback to creating all new slides
                    this.createNewLayout2Slides(images);
                }
                
                // Update swiper
                this.swiperInstance.update();
                this.swiperInstance.slideTo(0, 0);
                
            } catch (error) {
                console.error('[SG] Layout 2 gallery update failed:', error);
                // Try direct DOM manipulation as fallback
                this.updateLayout2MainGalleryDOM(images);
            }
            
            log('Layout 2 main gallery updated');
        }
        
        updateLayout2MainGalleryDOM(images) {
            // Direct DOM manipulation fallback for Layout 2
            const wrapper = document.querySelector('.main--product-image-slider.swiper-wrapper');
            if (!wrapper) {
                log('No wrapper found for DOM update');
                return;
            }
            
            const slides = wrapper.querySelectorAll('.swiper-slide');
            slides.forEach((slide, index) => {
                if (index < images.length) {
                    const img = slide.querySelector('img');
                    this._updateImg(img, images[index].src, images[index].alt, index === 0);
                }
            });

            log('Layout 2 DOM updated directly');
        }
        
        createNewLayout2Slides(images) {
            // Helper method to create all new slides when needed
            this.swiperInstance.removeAllSlides();
            
            images.forEach((image, index) => {
                let slideHtml;
                
                if (this.originalGalleryState.originalSlides[index]) {
                    slideHtml = this.originalGalleryState.originalSlides[index].html;
                    slideHtml = slideHtml.replace(/src="[^"]*"/, `src="${image.src}"`);
                    slideHtml = slideHtml.replace(/srcset="[^"]*"/, '');
                    slideHtml = slideHtml.replace(/data-src="[^"]*"/, `data-src="${image.src.replace(/width=\d+/, 'width={width}')}"`);
                    slideHtml = slideHtml.replace(/data-original="[^"]*"/, `data-original="${image.src}"`);
                    slideHtml = slideHtml.replace(/data-srcset="[^"]*"/, '');
                    if (image.alt) {
                        slideHtml = slideHtml.replace(/alt="[^"]*"/, `alt="${image.alt}"`);
                    }
                } else if (this.originalGalleryState.mainSlideTemplate) {
                    slideHtml = this.originalGalleryState.mainSlideTemplate;
                    slideHtml = slideHtml
                        .replace(/__IMAGE_SRC__/g, image.src)
                        .replace(/__IMAGE_ALT__/g, image.alt || '')
                        .replace(/__MEDIA_ID__/g, `${this.sectionId}-gallery-${index}`)
                        .replace(/__INDEX__/g, index.toString());
                } else {
                    slideHtml = `
                        <div class="swiper-slide main--product-item" data-media-id="${this.sectionId}-gallery-${index}">
                            <div class="media" ${this.originalGalleryState.imageRatio ? `style="--image_ratio: ${this.originalGalleryState.imageRatio}"` : ''}>
                                <img src="${image.src}" alt="${image.alt || ''}" loading="${index === 0 ? 'eager' : 'lazy'}">
                            </div>
                        </div>
                    `;
                }
                
                if (index === 0) {
                    slideHtml = slideHtml.replace(/loading="lazy"/g, 'loading="eager"');
                }
                
                this.swiperInstance.appendSlide(slideHtml);
            });
        }
        
        updateLayout2ThumbnailGallery(images) {
            if (!this.thumbSwiperInstance) {
                log('Missing thumbnail swiper instance');
                // Try direct DOM manipulation
                this.updateLayout2ThumbnailsDOM(images);
                return;
            }
            
            // Create offset array for thumbnails - shows next image instead of current
            const offsetImages = images.length > 1 
                ? [...images.slice(1), images[0]]
                : images;
            
            log('Updating Layout 2 thumbnails with offset:', offsetImages.length, 'images');
            
            try {
                // Get current thumbnail slides
                const currentThumbs = this.thumbSwiperInstance.slides;
                
                // Update existing thumbnails in place
                if (currentThumbs && currentThumbs.length > 0) {
                    currentThumbs.forEach((thumb, index) => {
                        if (index < offsetImages.length) {
                            const image = offsetImages[index];
                            const img = thumb.querySelector('img');
                            this._updateImg(img, image.src, image.alt, false);

                            // Update active class
                            if (index === 0) {
                                thumb.classList.add('swiper-slide-thumb-active');
                            } else {
                                thumb.classList.remove('swiper-slide-thumb-active');
                            }
                        }
                    });
                    
                    // Handle different number of thumbnails
                    if (offsetImages.length > currentThumbs.length) {
                        for (let i = currentThumbs.length; i < offsetImages.length; i++) {
                            const image = offsetImages[i];
                            let thumbHtml;
                            
                            if (this.originalGalleryState.thumbnailTemplate) {
                                thumbHtml = this.originalGalleryState.thumbnailTemplate;
                                thumbHtml = thumbHtml
                                    .replace(/__THUMB_SRC__/g, image.src)
                                    .replace(/__THUMB_ALT__/g, image.alt || '')
                                    .replace(/__THUMB_ID__/g, `${this.sectionId}-thumb-${i}`);
                            } else {
                                thumbHtml = `
                                    <div class="swiper-slide main--product-thumbnail-item" data-thumb-id="${this.sectionId}-thumb-${i}">
                                        <img src="${image.src}" alt="${image.alt || ''}" loading="lazy">
                                    </div>
                                `;
                            }
                            
                            this.thumbSwiperInstance.appendSlide(thumbHtml);
                        }
                    } else if (offsetImages.length < currentThumbs.length) {
                        for (let i = currentThumbs.length - 1; i >= offsetImages.length; i--) {
                            this.thumbSwiperInstance.removeSlide(i);
                        }
                    }
                } else {
                    // Create new thumbnails
                    this.createNewLayout2Thumbnails(offsetImages);
                }
                
                // Update and reset
                this.thumbSwiperInstance.update();
                this.thumbSwiperInstance.slideTo(0, 0);
                
            } catch (error) {
                console.error('[SG] Thumbnail update failed:', error);
                this.updateLayout2ThumbnailsDOM(offsetImages);
            }
            
            log('Layout 2 thumbnails updated');
        }
        
        updateLayout2ThumbnailsDOM(images) {
            // Direct DOM manipulation for thumbnails
            const wrapper = document.querySelector('.main--product-thumbnail-outer .swiper-wrapper');
            if (!wrapper) {
                log('No thumbnail wrapper found');
                return;
            }
            
            const offsetImages = images.length > 1 
                ? [...images.slice(1), images[0]]
                : images;
            
            const thumbs = wrapper.querySelectorAll('.swiper-slide');
            thumbs.forEach((thumb, index) => {
                if (index < offsetImages.length) {
                    const img = thumb.querySelector('img');
                    this._updateImg(img, offsetImages[index].src, offsetImages[index].alt, false);

                    if (index === 0) {
                        thumb.classList.add('swiper-slide-thumb-active');
                    } else {
                        thumb.classList.remove('swiper-slide-thumb-active');
                    }
                }
            });

            log('Thumbnails DOM updated directly');
        }
        
        createNewLayout2Thumbnails(offsetImages) {
            this.thumbSwiperInstance.removeAllSlides();
            
            offsetImages.forEach((image, index) => {
                let thumbHtml;
                
                if (this.originalGalleryState.originalThumbs[index]) {
                    thumbHtml = this.originalGalleryState.originalThumbs[index].html;
                    thumbHtml = thumbHtml.replace(/src="[^"]*"/, `src="${image.src}"`);
                    thumbHtml = thumbHtml.replace(/srcset="[^"]*"/, '');
                    thumbHtml = thumbHtml.replace(/data-src="[^"]*"/, `data-src="${image.src.replace(/width=\d+/, 'width={width}')}"`);
                    thumbHtml = thumbHtml.replace(/data-original="[^"]*"/, `data-original="${image.src}"`);
                    thumbHtml = thumbHtml.replace(/data-srcset="[^"]*"/, '');
                    if (image.alt) {
                        thumbHtml = thumbHtml.replace(/alt="[^"]*"/, `alt="${image.alt}"`);
                    }
                } else if (this.originalGalleryState.thumbnailTemplate) {
                    thumbHtml = this.originalGalleryState.thumbnailTemplate;
                    thumbHtml = thumbHtml
                        .replace(/__THUMB_SRC__/g, image.src)
                        .replace(/__THUMB_ALT__/g, image.alt || '')
                        .replace(/__THUMB_ID__/g, `${this.sectionId}-thumb-${index}`);
                } else {
                    thumbHtml = `
                        <div class="swiper-slide main--product-thumbnail-item" data-thumb-id="${this.sectionId}-thumb-${index}">
                            <img src="${image.src}" alt="${image.alt || ''}" loading="lazy">
                        </div>
                    `;
                }
                
                if (index === 0) {
                    thumbHtml = thumbHtml.replace('swiper-slide', 'swiper-slide swiper-slide-thumb-active');
                }
                
                this.thumbSwiperInstance.appendSlide(thumbHtml);
            });
        }
        
        setupLayout2GallerySync() {
            if (!this.swiperInstance || !this.thumbSwiperInstance) {
                log('Cannot setup Layout 2 sync - missing swiper instances');
                return;
            }

            // Remove only OUR previously-attached handler (tagged with _sgSync)
            // so we don't nuke the theme's own slideChange listeners (video pause, etc.)
            if (this._mainSlideChangeHandler) {
                this.swiperInstance.off('slideChange', this._mainSlideChangeHandler);
            }
            if (this._thumbClickHandler) {
                this.thumbSwiperInstance.off('click', this._thumbClickHandler);
            }

            // Sync main gallery → thumbnails using realIndex (loop-aware)
            this._mainSlideChangeHandler = () => {
                const realIdx = this.swiperInstance.realIndex;
                log('Main slideChange → realIndex:', realIdx);

                // Use slideToLoop so the thumb swiper respects its own loop
                this.thumbSwiperInstance.slideToLoop(realIdx, 300);

                // Update active thumbnail class
                this.thumbSwiperInstance.slides.forEach((slide, idx) => {
                    // In loop mode, match on realIndex-derived position
                    const slideRealIdx = parseInt(slide.getAttribute('data-swiper-slide-index'), 10);
                    const isActive = (!isNaN(slideRealIdx) ? slideRealIdx : idx) === realIdx;
                    slide.classList.toggle('swiper-slide-thumb-active', isActive);
                });
            };

            // Handle thumbnail clicks → main gallery
            this._thumbClickHandler = (swiper) => {
                const clickedSlide = swiper.clickedSlide;
                if (!clickedSlide) return;
                // Prefer the data-swiper-slide-index attr (loop-safe real index)
                const realIdx = parseInt(clickedSlide.getAttribute('data-swiper-slide-index'), 10);
                if (!isNaN(realIdx)) {
                    this.swiperInstance.slideToLoop(realIdx, 300);
                } else if (swiper.clickedIndex !== undefined && swiper.clickedIndex >= 0) {
                    this.swiperInstance.slideToLoop(swiper.clickedIndex, 300);
                }
            };

            this.swiperInstance.on('slideChange', this._mainSlideChangeHandler);
            this.thumbSwiperInstance.on('click', this._thumbClickHandler);

            log('Layout 2 gallery sync setup complete (loop-aware)');
        }
        
        swapDefaultGallery(images) {
            log('Swapping default gallery layout');
            
            // Update main gallery
            this.updateMainGallery(images);
            
            // Update thumbnail gallery with offset
            this.updateThumbnailGallery(images);
            
            // Setup synchronization between galleries
            this.setupGallerySync();
        }
        
        updateMainGallery(images) {
            const gallery = document.querySelector('.main--product-image-slider.swiper-wrapper') || 
                           document.querySelector('.swiper-wrapper');
            
            if (!gallery) {
                log('Gallery container not found');
                return;
            }
            
            if (this.swiperInstance) {
                this.updateSwiperWithImages(this.swiperInstance, images, false);
            } else {
                this.updateGalleryDOM(gallery, images);
            }
        }
        
        updateThumbnailGallery(images) {
            const thumbContainer = document.querySelector('.main--product-thumbnail-outer');
            if (thumbContainer && thumbContainer.swiper) {
                const thumbSwiper = thumbContainer.swiper;
                
                // Create offset thumbnail array - shows next image instead of current
                const offsetImages = images.length > 1 
                    ? [...images.slice(1), images[0]]
                    : images;
                
                log('Updating thumbnails with offset:', offsetImages.length, 'images');
                this.updateSwiperWithImages(thumbSwiper, offsetImages, true);
                
                this.thumbSwiperInstance = thumbSwiper;
            }
        }
        
        updateSwiperWithImages(swiper, images, isThumbnail = false) {
            log('Updating swiper with', images.length, 'images, thumbnail:', isThumbnail);
            
            try {
                // Remove all slides
                swiper.removeAllSlides();
                
                // Get the appropriate template and original slides
                const template = isThumbnail 
                    ? this.originalGalleryState.thumbnailTemplate 
                    : this.originalGalleryState.mainSlideTemplate;
                    
                const originalSlides = isThumbnail
                    ? this.originalGalleryState.originalThumbs
                    : this.originalGalleryState.originalSlides;
                
                // Create new slides
                images.forEach((image, index) => {
                    let slideHtml;
                    
                    // Try to use original slide structure if available
                    if (originalSlides && originalSlides[index]) {
                        slideHtml = originalSlides[index].html;
                        // Replace image src while preserving structure
                        slideHtml = slideHtml.replace(/src="[^"]*"/, `src="${image.src}"`);
                        slideHtml = slideHtml.replace(/srcset="[^"]*"/, '');
                        // Neutralize lazysizes data-* attributes
                        slideHtml = slideHtml.replace(/data-src="[^"]*"/, `data-src="${image.src.replace(/width=\d+/, 'width={width}')}"`);
                        slideHtml = slideHtml.replace(/data-original="[^"]*"/, `data-original="${image.src}"`);
                        slideHtml = slideHtml.replace(/data-srcset="[^"]*"/, '');
                        if (image.alt) {
                            slideHtml = slideHtml.replace(/alt="[^"]*"/, `alt="${image.alt}"`);
                        }
                    } else if (template && template.html) {
                        // Use template
                        slideHtml = template.html;
                        slideHtml = slideHtml.replace(/__IMAGE_SRC__/g, image.src);
                        slideHtml = slideHtml.replace(/__IMAGE_ALT__/g, image.alt || '');
                        slideHtml = slideHtml.replace(/<img[^>]*>/, `<img src="${image.src}" alt="${image.alt || ''}" loading="${index === 0 && !isThumbnail ? 'eager' : 'lazy'}">`);
                    } else {
                        // Fallback
                        slideHtml = `
                            <div class="swiper-slide">
                                <img src="${image.src}" alt="${image.alt || ''}" loading="${index === 0 && !isThumbnail ? 'eager' : 'lazy'}">
                            </div>
                        `;
                    }
                    
                    swiper.appendSlide(slideHtml);
                });
                
                // Update swiper
                swiper.update();
                swiper.slideTo(0, 0);
                
                log('Swiper updated successfully');
            } catch (error) {
                console.error('[SG] Swiper update failed:', error);
            }
        }
        
        updateGalleryDOM(container, images) {
            log('Updating gallery DOM with', images.length, 'images');
            
            const originalSlides = this.originalGalleryState.originalSlides;
            const template = this.originalGalleryState.mainSlideTemplate;
            
            const galleryHtml = images.map((image, index) => {
                if (originalSlides && originalSlides[index]) {
                    let slideHtml = originalSlides[index].html;
                    slideHtml = slideHtml.replace(/src="[^"]*"/, `src="${image.src}"`);
                    slideHtml = slideHtml.replace(/srcset="[^"]*"/, '');
                    slideHtml = slideHtml.replace(/data-src="[^"]*"/, `data-src="${image.src.replace(/width=\d+/, 'width={width}')}"`);
                    slideHtml = slideHtml.replace(/data-original="[^"]*"/, `data-original="${image.src}"`);
                    slideHtml = slideHtml.replace(/data-srcset="[^"]*"/, '');
                    if (image.alt) {
                        slideHtml = slideHtml.replace(/alt="[^"]*"/, `alt="${image.alt}"`);
                    }
                    return slideHtml;
                } else if (template && template.html) {
                    return template.html.replace(
                        /<img[^>]*>/,
                        `<img src="${image.src}" alt="${image.alt || ''}" loading="${index === 0 ? 'eager' : 'lazy'}">`
                    );
                } else {
                    return `
                        <div class="product__media-item" data-media-id="gallery-image-${index}">
                            <img src="${image.src}" alt="${image.alt || ''}" loading="lazy">
                        </div>
                    `;
                }
            }).join('');
            
            // Preserve container classes
            const originalClasses = this.originalGalleryState.containerClasses || container.className;
            container.innerHTML = galleryHtml;
            container.className = originalClasses;
            
            log('Gallery DOM updated');
        }
        
        setupGallerySync() {
            if (!this.swiperInstance || !this.thumbSwiperInstance) {
                log('Cannot setup sync - missing swiper instances');
                return;
            }

            // Remove only OUR previously-attached handlers (don't nuke theme listeners)
            if (this._defaultMainHandler) {
                this.swiperInstance.off('slideChange', this._defaultMainHandler);
            }
            if (this._defaultThumbHandler) {
                this.thumbSwiperInstance.off('click', this._defaultThumbHandler);
            }

            // Sync main gallery → thumbnails (loop-aware)
            this._defaultMainHandler = () => {
                const realIdx = this.swiperInstance.realIndex;
                log('Main gallery changed to realIndex:', realIdx);
                this.thumbSwiperInstance.slideToLoop(realIdx, 300);
            };

            // Sync thumbnail clicks → main gallery (loop-aware)
            this._defaultThumbHandler = (swiper) => {
                const clickedSlide = swiper.clickedSlide;
                if (!clickedSlide) return;
                const realIdx = parseInt(clickedSlide.getAttribute('data-swiper-slide-index'), 10);
                if (!isNaN(realIdx)) {
                    log('Thumbnail clicked, realIndex:', realIdx);
                    this.swiperInstance.slideToLoop(realIdx, 300);
                } else if (swiper.clickedIndex !== undefined && swiper.clickedIndex >= 0) {
                    this.swiperInstance.slideToLoop(swiper.clickedIndex, 300);
                }
            };
            
            // Set initial active thumbnail
            const currentMainIndex = this.swiperInstance.activeIndex || 0;
            this.thumbSwiperInstance.slideTo(currentMainIndex, 0);
            
            log('Gallery sync setup complete');
        }
        
        syncThemeStyles() {
            // Sync any runtime style changes to our swapped gallery
            const mainGallery = document.querySelector('.main--product-image-slider.swiper-wrapper');
            const sectionElement = document.querySelector('[id^="shopify-section-"]');
            
            if (mainGallery && sectionElement) {
                const sectionStyles = getComputedStyle(sectionElement);
                
                // Apply any custom properties that might have changed
                const stylesToSync = [
                    '--image-ratio',
                    '--carousel-border-radius',
                    '--card_radius',
                    '--top_spacing',
                    '--bottom_spacing'
                ];
                
                stylesToSync.forEach(prop => {
                    const value = sectionStyles.getPropertyValue(prop);
                    if (value) {
                        mainGallery.style.setProperty(prop, value);
                    }
                });
            }
        }
        
        triggerGalleryUpdate() {
            // Trigger various events that theme might be listening to
            ['gallery:update', 'media:update', 'variant:mediaChange'].forEach(eventName => {
                document.dispatchEvent(new CustomEvent(eventName));
            });
            
            // Trigger resize for recalculation
            window.dispatchEvent(new Event('resize'));
            
            // If in theme editor, trigger section refresh
            if (window.Shopify && window.Shopify.designMode) {
                document.dispatchEvent(new CustomEvent('shopify:section:update'));
            }
        }
        
        watchThemeEditor() {
            // Watch for Shopify theme editor events
            if (window.Shopify && window.Shopify.designMode) {
                log('Theme editor detected, setting up watchers');
                
                // Listen for section updates
                document.addEventListener('shopify:section:load', (e) => {
                    log('Section loaded:', e.detail);
                    this.handleThemeUpdate();
                });
                
                document.addEventListener('shopify:section:reorder', (e) => {
                    log('Section reordered:', e.detail);
                    this.handleThemeUpdate();
                });
                
                // Listen for setting changes
                window.addEventListener('shopify:settings:update', (e) => {
                    log('Settings updated:', e.detail);
                    this.handleThemeUpdate();
                });
            }
        }
        
        handleThemeUpdate() {
            log('Handling theme update');
            
            // Recapture the original state if structure changed
            if (!this.currentColor) {
                this.captureOriginalState();
            }
            
            // Reapply current color with new styles
            if (this.currentColor) {
                this.swapGallery(this.currentColor);
            }
        }
        
        // Public method for manual testing
        renderFor(colorName) {
            log('Manual render requested for:', colorName);
            this.handleColorChange(colorName);
        }
    }
    
    // Initialize SwatchGallery
    window.SwatchGallery = new SwatchGallery();
    
    // Expose for debugging
    window.testSwatchGallery = function(color) {
        console.log('[SG] Manual test for color:', color);
        window.SwatchGallery.renderFor(color);
    };
    
    log('Script setup complete');
})();

console.log('[SG] Fixed image mapping script fully loaded');